
/** Framework Libraries **/
import { Component, Vue } from "vue-property-decorator";

/** Components **/
import { mapActions, mapState } from "vuex";
import Unknown from "./components/Unknown.vue";
import Cna from "./components/Cna.vue";
import ComponentTest from "./components/ComponentTest.vue";
import DataBroker from "@/components/DataBroker.vue";
import MatchTable from "@/components/MatchTable.vue";
import Fixture from "@/components/Fixture.vue";
import Match from "@/components/Match.vue";
import PointsTable from "@/components/PointsTable.vue";
import Navbar from "@/components/Navbar.vue";
import Knockout from "@/components/Knockout.vue";
import WhereToWatch from "@/components/WhereToWatch.vue";
import MostRelevant from "@/components/MostRelevant.vue";

/** External Libraries **/
import DataApi from "./libs/DataApi";
import { Duct } from "@/libs/Duct";
import { UiNotification } from "@/libs/UiNotification";
import DataRepoOption from "@/libs/world-cup/DataRepoOption";
import DataApiPreloader from "@/libs/DataApiPreloader";
import DataApiPostloader from "@/libs/DataApiPostloader";
import * as lob from "lodash/object";
import MainApp from "@/libs/MainApp";
import StatusAware from "@/libs/common/delegate/StatusAware";
import StorePayload from "@/libs/model/StorePayload";
import ChecksumAware from "@/libs/common/delegate/ChecksumAware";
import StoreNames from "@/store-names";

declare let $: any;


@Component({
  // Component pre-registrations.
  components: {
    Unknown,
    Cna,
    ComponentTest,
    DataBroker,
    MatchTable,
    Fixture,
    Match,
    PointsTable,
    Navbar,
    Knockout,
    WhereToWatch,
    MostRelevant
  },
  computed: {
    ...mapState({
      configuration: "configuration",
      preloadedRepos: "preloadedRepos",
      lastUpdate: "lastUpdate",
      translations: "translations",
      checksum: "checksum",
      status: "status",
      worldCupMatches: "worldCupMatches",
      pointsTable: "pointsTable",
    }),
  },
  methods: {
    ...mapActions({
      addPreloadedRepo: "addPreloadedRepo",
      setLastUpdate: "setLastUpdate",
      setTranslations: "setTranslations",
      setIsPreloadDone: "setIsPreloadDone",
      setChecksum: "setChecksum",
      setStatus: "setStatus",
      setWorldCupMatches: "setWorldCupMatches",
      setPointsTable: "setPointsTable",
    }),
  },
})
export default class App extends MainApp implements StatusAware, ChecksumAware {
  // Data variable declarations
  window: any;
  $root: MainApp;
  component: string;
  info: any;
  dataApiPreloader: DataApiPreloader;
  dataApiPostloader: DataApiPostloader;
  dataRepo: DataRepoOption[];
  configuration: any;
  $years: number[];
  componentVisible: boolean;

  // Store object declarations
  preloadedRepo: string[];
  lastUpdate: number;
  translations: any;
  constituencySeatCount: any;
  checksum: any;
  status: any;
  worldCupMatches: any;
  pointsTable: any;

  // Setter declarations
  addPreloadedRepo: (o: StorePayload) => void;
  setLastUpdate: (o: StorePayload) => void;
  setTranslations: (o: StorePayload) => void;
  setIsPreloadDone: (o: StorePayload) => void;
  setChecksum: (o: StorePayload) => void;
  setStatus: (o: StorePayload) => void;
  setWorldCupMatches: (o: StorePayload) => void;
  setPointsTable: (o: StorePayload) => void;

  // Function/method declarations
  dataReadyCallback: Function;

  created() {
    /**
     * All listing of data must be pre-loaded here.
     * This is to prevent multiple unnecessary requests to the server
     * during component render.
     */

    let that = this;
    this.dataRepo = this.$root.$config.dataRepo;
    this.componentVisible = this.$root.$config.option.visible;

    // Generic listener for application-wide errors.
    // Flash messages should only be handled by one instance only.
    Duct.open(Duct.Vanes.OnError, this.onError);

    // Initialize preloader class. This will handle
    // all our data preloading needs
    this.dataApiPreloader = new DataApiPreloader({
      dataApi: new DataApi(this.$root.$config.dataPoint), // Reponsible for configuring our REST api
      dataRepoOptions: this.dataRepo,
      onReady: this.onPreloadDataReady,
      onPreload: this.onPreload,
      onPreloadDone: this.onPreloadDone,
      onError: this.onPreloadRequestError,
      uid: this.$root.$config.uid,
    });

    // Handles consecutive long pulling.
    this.dataApiPostloader = new DataApiPostloader({
      dataApi: new DataApi(this.$root.$config.dataPoint), // Reponsible for configuring our REST api
      dataRepoOptions: this.dataRepo,
      timeout: this.$root.$config.dataPoint.timeout,
      onReady: this.onPostloadDataReady,
      onPostloadDone: this.onPostloadDone,
      uid: this.$root.$config.uid,
      statusAware: this,
    });
  }

  mounted() {
    let that = this;

    // Activate our super mega hyper monumental magic script that handles all the dirty work of pre-loading
    // data on the background without you ( whoever you are ) lifting any finger.
    // If you love my work do please comment on github, or send me an email on how awesome this is.
    // However, if you do feel it is over-engineered feel free to send me an email or contribute to this branch.
    this.$nextTick(function () {
      if (that.$root.$config.dataPoint.preload) {
        that.dataApiPreloader.preload();
      }
    });

    // Added in case child want to listen for parent mounted event as the child is mounted first.
    Duct.vent(Duct.Vanes.OnAppMounted);

    // Handle Bootstrap.js Modal
    this.modalExtraHooks();
  }

  /**
   * Extra hooks for bootstrap modal.
   */
  modalExtraHooks() {
    // Prevent background scrolling
    $("[ge-selector].modal")
      .on("show.bs.modal", function () {
        let activeClass = `${$(this).attr("id")}-modal-active`;
        $("html").removeClass(activeClass).addClass(activeClass);
      })
      .on("hide.bs.modal", function () {
        let activeClass = `${$(this).attr("id")}-modal-active`;
        $("html").removeClass(activeClass);
      });
  }

  destroyed() {
    // Disable listener when a component is destroyed.
    Duct.close(Duct.Vanes.OnError, this.onError);
    Duct.close(Duct.Vanes.OnAppMounted);
  }

  onDebug(owner: any, name: string, context?: any) {
    if (
      this.$root.$config.option.debug.callback === undefined ||
      this.$root.$config.option.debug.callback === null
    ) {
      return;
    }

    this.$root.$config.option.debug.callback(owner, name, context);
  }

  updateStore(name: string, data: any) {
    const that = this;
    this.onDebug(that, `updateStore:${name}`, data);

    if (name === "labels") {
      if (data === undefined || data === null) {
        return;
      }

      let translationData = lob.get(this.translations, that.$root.$config.year);
      if (translationData !== undefined && translationData !== null) {
        data = lob.merge(data, translationData);
      }

      let customTranslations = lob.get(this.$root.$config.data, "translations");
      if (customTranslations !== undefined && translationData !== null) {
        data = lob.merge(data, customTranslations);
      }

      this.setTranslations({ year: this.$root.$config.year, data: data });

      return;
    }

    if (name === "externalTranslation") {
      if (data === undefined || data === null) {
        return;
      }

      let translationData = lob.get(this.translations, that.$root.$config.year);
      if (translationData !== undefined && translationData !== null) {
        data = lob.merge(translationData, data);
      }

      let customTranslations = lob.get(this.$root.$config.data, "translations");
      if (customTranslations !== undefined && translationData !== null) {
        data = lob.merge(data, customTranslations);
      }

      this.setTranslations({ year: this.$root.$config.year, data: data });

      return;
    }

    if (name === "checksum") {
      let currentHash = this.getChecksumByName("average");
      let newHash = lob.get(data, "average");

      if (currentHash === undefined) {
        that.setChecksum({ year: that.$root.$config.year, data: data });
        that.setStatusByName("dataRepo", "new");
        return;
      }

      if (newHash !== currentHash) {
        that.setChecksum({ year: that.$root.$config.year, data: data });
        that.setStatusByName("dataRepo", "new");
        return;
      }

      // Unchanged
      that.setStatusByName("dataRepo", "unchanged");
      return;
    }

    if (name === "worldCupMatches") {
      this.setWorldCupMatches({
        year: this.$root.$config.year,
        data: data,
      });
      return;
    }

    if (name === "pointsTable") {
      this.setPointsTable({
        year: this.$root.$config.year,
        data: data,
      });
      return;
    }
  }

  ventOut(name: string) {
    // Generic data ready
    Duct.vent(Duct.Vanes.OnDataReady, name);

    // Specific data ready
    let vanes = {
      worldCupMatches: Duct.Vanes.OnWorldCupMatchesReady,
      pointsTable: "OnPointsTableReady",
    };

    let vane = vanes[name];
    if (vane !== undefined) {
      Duct.vent(name, vane);
    }
  }

  onPreloadDone() {
    this.onDebug(this, "preloadDone", {});
    Duct.vent(Duct.Vanes.OnPreloadDone);
    this.setLastUpdate({ data: Date.now(), year: this.$root.$config.year });
    this.setIsPreloadDone({ data: true, year: this.$root.$config.year });

    // Activate our long pulling mechanism
    if (this.$root.$config.dataPoint.postload) {
      this.dataApiPostloader.postload();
    }
  }

  onError(args: any) {
    let that = this;
    this.onDebug(that, "error", args);
    let notification = lob.get(
      this.$root.$config.option.notification.show,
      true
    );

    if (notification) {
      that.onErrorNotification(args);
    }
  }

  onErrorNotification(args: any) {
    if (args === undefined) {
      UiNotification.error("Oops! Something went wrong. :(");
      return;
    }

    if (typeof args === "string") {
      UiNotification.error("ERROR: " + args);
      return;
    }

    let json = JSON.stringify(args);
    UiNotification.error(json);
  }

  onPreload(name: string) {
    this.addPreloadedRepo({ data: name, year: this.$root.$config.year });
  }

  /**
   * This method will be called when all required data are ready.
   *
   * For components that require multiple sources of data, this ensures that data from all those sources are loaded
   * and ready for consumption before you can actually instruct your component to anything.
   *
   * @param name
   * @param data
   */
  onPreloadDataReady(name: string, data?: any) {
    this.updateStore(name, data);
    this.ventOut(name);

    if (
      this.dataReadyCallback !== undefined &&
      this.dataReadyCallback !== null
    ) {
      this.dataReadyCallback("preload", {
        data: data,
        uid: this.$root.$config.uid,
        name: this.$root.$config.component,
        year: this.$root.$config.year,
        component: this,
        store: name,
      });
    }
  }

  onPreloadRequestError(name: string, data: any): void {
    this.onDebug(this, `onPreloadRequestError:${name}`, data);

    if (this.$root.$config.option.notification.show) {
      UiNotification.error(
        `Oops! Something went wrong: Request error - ${name}`
      );
    }
  }

  onPostloadDataReady(name: string, data: any) {
    this.updateStore(name, data);
    this.ventOut(name);
    if (
      this.dataReadyCallback !== undefined &&
      this.dataReadyCallback !== null
    ) {
      this.dataReadyCallback("postload", {
        data: data,
        uid: this.$root.$config.uid,
        name: this.$root.$config.component,
        component: this,
        year: this.$root.$config.year,
        store: name,
      });
    }
  }

  onPostloadDone() {
    this.onDebug(this, "onPostloadDone", {});
    Duct.vent(Duct.Vanes.OnPostloadDone);
    this.setLastUpdate({ year: this.$root.$config.year, data: Date.now() });
  }

  getStatusByName(name: string, value?: any): string {
    if (this.status === undefined) {
      return value;
    }

    let status = lob.get(this.status, this.$root.$config.year);
    if (status === undefined) {
      return value;
    }

    return lob.get(status, name, value);
  }

  setStatusByName(name: string, value: any): void {
    if (this.status === undefined) {
      return;
    }

    let status = lob.get(this.status, this.$root.$config.year);
    if (status === undefined) {
      status = {};
      status = lob.set(status, name, value);
      this.setStatus({ data: status, year: this.$root.$config.year });
      return;
    }

    status = lob.set(status, name, value);
    this.setStatus({ data: status, year: this.$root.$config.year });
  }

  getChecksumByName(name: string): string {
    const that = this;
    if (that.checksum === undefined) {
      return undefined;
    }

    let checksum = lob.get(this.checksum, this.$root.$config.year);
    if (checksum === undefined) {
      return undefined;
    }

    return lob.get(checksum, name);
  }
  setChecksumByName(name: string, hash: string): void {
    if (this.checksum === undefined) {
      return;
    }

    let checksum = lob.get(this.checksum, this.$root.$config.year);
    if (checksum === undefined) {
      return;
    }

    checksum = lob.set(checksum, name, hash);

    this.setChecksum({ data: checksum, year: this.$root.$config.year });
  }

  stop() {
    this.dataApiPostloader.stop();
  }

  start() {
    this.dataApiPostloader.start();
  }

  setTimeout(timeout: number) {
    this.dataApiPostloader.setTimeout(timeout);
  }

  getStore(name: string): any {
    if (!StoreNames.includes(name)) {
      return undefined;
    }

    let storedObject = lob.get(this, name);
    if (storedObject === undefined || storedObject === null) {
      return undefined;
    }

    return storedObject[this.$root.$config.year];
  }

  getStoreNames(): string[] {
    return StoreNames;
  }

  getComponentVisibility(): boolean {
    return this.componentVisible;
  }

  hide() {
    this.componentVisible = false;
    this.$forceUpdate();
  }

  show() {
    this.componentVisible = true;
    this.$forceUpdate();
  }

  onDataReady(callback: Function) {
    this.dataReadyCallback = callback;
  }
}
